import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Sparkles, ChevronRight, X, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_STEP = "trademind:tour-step";
const LS_LATER = "trademind:tour-later-at";

/**
 * Coach-geführte Product-Tour durch die 7 Hauptbereiche.
 *
 * Erster Auftritt: Coach stellt sich vor und lädt zur Tour ein. Nutzer kann
 * jederzeit «Später ansehen» oder «Überspringen». Bei Fertigstellung wird
 * `users.tour.completed_at` gesetzt und die Tour zeigt sich nie wieder.
 */
const STEPS = [
  { key: "intro", route: null,
    title: "Hi, ich bin dein KI-Coach.",
    body: "Ich zeige dir in unter einer Minute die sieben Bereiche der App. Du kannst jederzeit abbrechen und die Tour später in den Einstellungen fortsetzen." },
  { key: "dashboard", route: "/app",
    title: "1. Übersicht",
    body: "Deine tägliche Startseite. Hier siehst du auf einen Blick: offene Trades, Regeltreue und Erinnerungen. Der Fokus liegt bewusst auf deinem Prozess, nicht nur auf Gewinn und Verlust." },
  { key: "journal", route: "/app/journal",
    title: "2. Trading Journal",
    body: "Hier dokumentierst du jeden Trade mit Setup, Regeln, Stimmung und Reflexion. Trades von deinen Brokern werden automatisch importiert — deine Notizen ergänzt du selbst." },
  { key: "strategien", route: "/app/strategien",
    title: "3. Strategien",
    body: "Halte hier deine Setups und Handelsregeln fest. Ich helfe dir, sie zu schärfen und Widersprüche zu erkennen — die Basis für einen wiederholbaren Ablauf." },
  { key: "analyse", route: "/app/analyse",
    title: "4. Analyse",
    body: "Trefferquote, R-Verlauf, Trade-Verteilung und Skill-Score im Überblick. Die Zahlen sind Feedback für dich, kein Urteil — sie zeigen, woran du arbeiten kannst." },
  { key: "ki-coach", route: "/app/ki-coach",
    title: "5. KI-Coach",
    body: "Hier sprechen wir uns. Frag mich zu Setups, Reflexion oder wiederkehrenden Mustern. Du kannst auch Chart-Screenshots anhängen, die wir gemeinsam durchgehen." },
  { key: "steuer", route: "/app/steuer",
    title: "6. Steuer",
    body: "Deine deutsche Krypto-Steuer auf einen Klick: Spot-Gewinne für die Anlage SO, Derivate für die Anlage KAP. TradeMind erstellt daraus einen fertigen PDF-Bericht fürs Finanzamt." },
  { key: "einstellungen", route: "/app/einstellungen",
    title: "7. Einstellungen",
    body: "Broker verbinden (OKX, Bitget, Binance, Kraken, Bitunix), Design anpassen, Support kontaktieren und dein Steuer-Profil pflegen — alles an einer Stelle." },
  { key: "done", route: "/app",
    title: "Fertig — du bist startklar.",
    body: "Das war die Tour. Wenn du magst, sprechen wir jetzt direkt im KI-Coach weiter — ich passe mich deinem Wissensstand automatisch an." },
];

export default function ProductTour() {
  const { user, refresh } = useAuth() || {};
  const nav = useNavigate();
  const loc = useLocation();

  const [step, setStep] = useState(() => {
    try { return Number(localStorage.getItem(LS_STEP) || 0); } catch { return 0; }
  });
  const [saving, setSaving] = useState(false);
  const [snoozed, setSnoozed] = useState(() => {
    try {
      const t = Number(localStorage.getItem(LS_LATER) || 0);
      return t && Date.now() - t < 24 * 60 * 60 * 1000;
    } catch { return false; }
  });

  const alreadyDone = !!user?.tour?.completed_at;
  const inAppArea = loc.pathname.startsWith("/app");
  const shouldShow = inAppArea && !!user && !alreadyDone && !snoozed;

  const persist = useCallback(async (status) => {
    setSaving(true);
    try {
      await axios.post(`${API}/auth/me/tour`, { status }, { withCredentials: true });
      if (refresh) await refresh();
      try { localStorage.removeItem(LS_STEP); } catch {}
    } catch { /* still hide client-side */ }
    finally { setSaving(false); }
  }, [refresh]);

  const persistStep = (n) => {
    try { localStorage.setItem(LS_STEP, String(n)); } catch {}
  };

  const goNext = () => {
    const next = step + 1;
    if (next >= STEPS.length) {
      persist("completed");
      return;
    }
    const target = STEPS[next].route;
    setStep(next);
    persistStep(next);
    if (target && target !== loc.pathname) nav(target);
  };

  const goBack = () => {
    const prev = Math.max(0, step - 1);
    setStep(prev);
    persistStep(prev);
    const target = STEPS[prev].route;
    if (target && target !== loc.pathname) nav(target);
  };

  const later = async () => {
    try { localStorage.setItem(LS_LATER, String(Date.now())); } catch {}
    setSnoozed(true);
    persist("later");
  };

  const skip = () => {
    if (window.confirm("Tour überspringen? Du findest sie später jederzeit in den Einstellungen wieder.")) {
      persist("skipped");
    }
  };

  const current = useMemo(() => STEPS[step] || STEPS[0], [step]);

  // Beim ersten Zeigen: wenn nicht auf /app, dorthin springen
  useEffect(() => {
    if (shouldShow && step === 0 && loc.pathname !== "/app" && loc.pathname !== "/app/") {
      // sanft — kein Auto-Navigate im Intro, Nutzer kann selbst zu /app gehen
    }
  }, [shouldShow, step, loc.pathname]);

  if (!shouldShow) return null;

  const isLast = step === STEPS.length - 1;
  const isFirst = step === 0;

  return (
    <div
      className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-[70] w-[calc(100vw-2rem)] max-w-md"
      data-testid="product-tour"
    >
      <div className="rounded-2xl border border-primary/40 bg-background/95 backdrop-blur-xl shadow-2xl p-5 md:p-6">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-full bg-primary/15 border border-primary/40 grid place-items-center shrink-0">
            <Sparkles className="h-5 w-5 text-primary" strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
                Coach · Schritt {Math.min(step + 1, STEPS.length)} / {STEPS.length}
              </span>
              <button
                type="button"
                onClick={later}
                className="text-muted-foreground hover:text-foreground -mr-1"
                aria-label="Tour später fortsetzen"
                data-testid="tour-close"
              >
                <X className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </div>
            <h3 className="font-heading text-lg mt-1" data-testid="tour-title">{current.title}</h3>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed" data-testid="tour-body">
              {current.body}
            </p>

            {/* Fortschritts-Balken */}
            <div className="mt-4 h-1 rounded-full bg-secondary overflow-hidden">
              <div
                className="h-full bg-primary transition-all duration-500"
                style={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
                data-testid="tour-progress"
              />
            </div>

            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <Button
                onClick={goNext}
                disabled={saving}
                className="rounded-full h-10"
                data-testid="tour-next"
              >
                {isLast ? (
                  <><Check className="mr-2 h-4 w-4" strokeWidth={1.5} /> Fertig, los geht's</>
                ) : (
                  <>{isFirst ? "Tour starten" : "Weiter"} <ChevronRight className="ml-1 h-4 w-4" strokeWidth={1.5} /></>
                )}
              </Button>
              {!isFirst && !isLast && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={goBack}
                  className="rounded-full"
                  data-testid="tour-back"
                >
                  Zurück
                </Button>
              )}
              <button
                type="button"
                onClick={later}
                className="text-xs text-muted-foreground hover:text-foreground ml-auto"
                data-testid="tour-later"
              >
                Später ansehen
              </button>
              <button
                type="button"
                onClick={skip}
                className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-4"
                data-testid="tour-skip"
              >
                Überspringen
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
